import { Box } from '@components/Box';
import { BsFileEarmarkText } from 'react-icons/bs';
import { IoClose } from 'react-icons/io5';
import { LoadingSpinner } from '@components/Icons';
import { useState } from 'react';

export interface FileWithPreview extends File {
  preview?: string;
}

interface AttachmentProps {
  file: FileWithPreview;
  onRemove: () => void;
}

const Attachment = ({ file, onRemove }: AttachmentProps) => {
  const isImage = file.type.startsWith('image/') && file.preview;
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
  const fileName = file.name.split('.').slice(0, -1).join('.');
  const [isImageLoading, setIsImageLoading] = useState(true);

  return (
    <Box
      className={`relative group rounded-7 flex-shrink-0 overflow-hidden border border-elz-neutral-6 ${
        isImage ? 'size-[48px]' : 'w-[96px] h-[48px]'
      }`}
    >
      {isImage ? (
        <>
          {isImageLoading && (
            <Box className="absolute inset-0 flex items-center justify-center bg-elz-neutral-3">
              <LoadingSpinner />
            </Box>
          )}
          <img
            src={file.preview}
            alt={file.name}
            className={`w-full h-full object-cover transition-opacity duration-200 ${
              isImageLoading ? 'opacity-0' : 'opacity-100'
            }`}
            onLoad={() => setIsImageLoading(false)}
          />
        </>
      ) : (
        <Box className="flex flex-col size-full items-start justify-center bg-elz-neutral-3 p-2 gap-2">
          <div className="flex flex-row gap-1">
            <BsFileEarmarkText className="size-15 text-elz-neutral-8" />
            <span className="text-elz-neutral-11 text-[10px]">
              {fileExtension}
            </span>
          </div>
          <span
            title={fileName}
            className="text-elz-neutral-11 text-[10px] max-w-full truncate px-1"
          >
            {fileName}
          </span>
        </Box>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-0 right-0 size-15 rounded-full bg-elz-neutral-12 text-elz-neutral-2 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <IoClose className="size-10" />
      </button>
    </Box>
  );
};

interface AttachmentsListProps {
  files: FileWithPreview[];
  onRemoveFile: (index: number) => void;
}

const AttachmentsList = ({ files, onRemoveFile }: AttachmentsListProps) => {
  if (files.length === 0) return null;

  return (
    <Box className="flex flex-row gap-6 overflow-x-auto pb-2">
      {files.map((file, index) => (
        <Attachment
          key={`${file.name}-${index}`}
          file={file}
          onRemove={() => onRemoveFile(index)}
        />
      ))}
    </Box>
  );
};

export default AttachmentsList;
